import type { ApiComponents } from '@aztec-realms/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { browserApiClient, requiredApiData } from './browser-api';
import {
  tribeAssistanceQueryKey,
  tribeExpeditionQueryKey,
  tribeSocialQueryKey,
} from './tribe-query-keys';
import { verticalSliceQueryKey } from './use-vertical-slice';
import { inboxQueryKey } from './earned-inbox-api';

export type AssistanceRequest = ApiComponents['schemas']['AssistanceRequestResponse'];
export type AssistanceCategory = AssistanceRequest['category'];
export type AssistanceCommand =
  | {
      type: 'create';
      commandId: string;
      targetKind: AssistanceRequest['target_kind'];
      targetId: string;
      category: AssistanceCategory;
    }
  | { type: 'help' | 'withdraw' | 'cancel'; commandId: string; requestId: string }
  | { type: 'thanks'; commandId: string; requestId: string; helperMemberId: string }
  | {
      type: 'report';
      commandId: string;
      requestId: string;
      reason: ApiComponents['schemas']['AssistanceReportRequest']['reason'];
    }
  | { type: 'block'; commandId: string; memberId: string }
  | { type: 'unblock'; commandId: string; blockId: string };

export async function loadTribeAssistance(tribeId: string, signal?: AbortSignal) {
  const response = await browserApiClient().GET('/api/v1/tribes/{tribe_id}/assistance', {
    params: { path: { tribe_id: tribeId } },
    signal,
  });
  return requiredApiData(
    response.data,
    response.error,
    'Your Tribe’s requests could not be loaded.',
  );
}

export async function submitAssistanceCommand(tribeId: string, command: AssistanceCommand) {
  const client = browserApiClient({ mutation: true });
  const header = { 'Idempotency-Key': command.commandId };
  const path = { tribe_id: tribeId };
  const response =
    command.type === 'create'
      ? await client.POST('/api/v1/tribes/{tribe_id}/assistance', {
          params: { path, header },
          body: {
            target_kind: command.targetKind,
            target_id: command.targetId,
            category: command.category,
          },
        })
      : command.type === 'block'
        ? await client.POST('/api/v1/tribes/{tribe_id}/assistance/blocks', {
            params: { path, header },
            body: { member_id: command.memberId },
          })
        : command.type === 'unblock'
          ? await client.POST('/api/v1/tribes/{tribe_id}/assistance/blocks/{block_id}/remove', {
              params: { path: { ...path, block_id: command.blockId }, header },
              body: {},
            })
          : command.type === 'withdraw'
            ? await client.POST('/api/v1/tribes/{tribe_id}/assistance/{request_id}/help/withdraw', {
                params: { path: { ...path, request_id: command.requestId }, header },
                body: {},
              })
            : command.type === 'thanks'
              ? await client.POST('/api/v1/tribes/{tribe_id}/assistance/{request_id}/thanks', {
                  params: { path: { ...path, request_id: command.requestId }, header },
                  body: { helper_member_id: command.helperMemberId },
                })
              : command.type === 'report'
                ? await client.POST('/api/v1/tribes/{tribe_id}/assistance/{request_id}/report', {
                    params: { path: { ...path, request_id: command.requestId }, header },
                    body: { reason: command.reason },
                  })
                : command.type === 'help'
                  ? await client.POST('/api/v1/tribes/{tribe_id}/assistance/{request_id}/help', {
                      params: { path: { ...path, request_id: command.requestId }, header },
                      body: {},
                    })
                  : await client.POST('/api/v1/tribes/{tribe_id}/assistance/{request_id}/cancel', {
                      params: { path: { ...path, request_id: command.requestId }, header },
                      body: {},
                    });
  return requiredApiData(
    response.data,
    response.error,
    'Your assistance action could not be confirmed.',
  );
}

export function useTribeAssistance(tribeId: string, enabled: boolean) {
  return useQuery({
    queryKey: tribeAssistanceQueryKey(tribeId),
    queryFn: ({ signal }) => loadTribeAssistance(tribeId, signal),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useAssistanceCommand(tribeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Never queue a social command for an automatic submission after reconnecting.
    networkMode: 'always',
    retry: false,
    mutationFn: (command: AssistanceCommand) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('Reconnect before asking for help or responding to your Tribe.');
      }
      return submitAssistanceCommand(tribeId, command);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: inboxQueryKey }),
        queryClient.invalidateQueries({ queryKey: tribeAssistanceQueryKey(tribeId), exact: true }),
        queryClient.invalidateQueries({ queryKey: tribeExpeditionQueryKey(tribeId), exact: true }),
        queryClient.invalidateQueries({ queryKey: tribeSocialQueryKey(tribeId), exact: true }),
        queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey }),
      ]);
    },
  });
}
